import {
  createUnifiedTheme,
  createBaseThemeOptions,
  genPageTheme,
  palettes,
  shapes,
  type UnifiedTheme,
} from '@backstage/theme';
import { colors, elevation, rounded, typography } from './tokens';

/**
 * Pivotree-branded light theme for the Backstage portal.
 *
 * It is a worked example of applying `DESIGN.md` (the brand source of truth) to
 * a Material UI v4 surface. The strategy is to *extend* Backstage's default
 * light palette rather than replace it, so portal-specific palette keys
 * (navigation, banner, status, etc.) keep working; only the brand-relevant
 * slots are overridden from `./tokens`.
 *
 * Palette intent (per DESIGN.md):
 * - Green is the primary action color in app surfaces -> MUI `primary`.
 * - Navy is the workhorse for chrome (sidebar, links) -> dedicated palette keys.
 * - Highlight orange stays reserved; it is only wired to `warning` feedback,
 *   matching DESIGN.md's banner-warning / toast-warning usage.
 * - Light-only: no dark variant is exposed (DESIGN.md "Don't" rule).
 */

const pivotreePalette = {
  ...palettes.light,

  // Primary action = brand green (button-primary, choice-control "on" state).
  primary: {
    main: colors.brandSecondaryPure,
    dark: colors.brandSecondaryDark,
    light: colors.brandSecondaryMedium,
    contrastText: colors.neutralHighPure,
  },
  // Secondary = navy.
  secondary: {
    main: colors.brandPrimaryPure,
    dark: colors.brandPrimaryDark,
    light: colors.brandPrimaryMedium,
    contrastText: colors.neutralHighPure,
  },

  // Feedback families (DESIGN.md `feedback-*` + reserved highlight for warning).
  error: {
    main: colors.feedbackWarningPure,
    dark: colors.feedbackWarningDark,
    light: colors.feedbackWarningMedium,
    contrastText: colors.neutralHighPure,
  },
  warning: {
    main: colors.highlightPure,
    dark: colors.highlightDark,
    light: colors.highlightMedium,
    contrastText: colors.neutralHighPure,
  },
  success: {
    main: colors.feedbackSuccessPure,
    dark: colors.feedbackSuccessDark,
    light: colors.feedbackSuccessMedium,
    contrastText: colors.neutralHighPure,
  },
  info: {
    main: colors.feedbackInfoPure,
    dark: colors.feedbackInfoDark,
    light: colors.feedbackInfoMedium,
    contrastText: colors.neutralHighPure,
  },

  background: {
    ...palettes.light.background,
    default: colors.neutralHighLight, // surface-subtle (#F9F9F9)
    paper: colors.neutralHighPure, // surface-page (#FFFFFF)
  },
  text: {
    // `palettes.light` has no `text` key to spread; set the brand slots directly.
    primary: colors.neutralLowPure, // text-default (#333)
    secondary: colors.neutralLowLight, // text-muted (#575757)
  },

  // App shell / sidebar = navy (DESIGN.md `app-header` / `footer` / `app-nav-active`).
  navigation: {
    ...palettes.light.navigation,
    background: colors.brandPrimaryPure,
    color: colors.brandPrimaryLight,
    selectedColor: colors.neutralHighPure,
    indicator: colors.brandSecondaryPure, // green accent bar
    navItem: {
      hoverBackground: colors.brandPrimaryDark,
    },
  },

  // Links read navy (DESIGN.md `link-default`).
  link: colors.brandPrimaryPure,
  linkHover: colors.brandPrimaryDark,

  // Status dots / chips map to the feedback families.
  status: {
    ...palettes.light.status,
    ok: colors.feedbackSuccessPure,
    warning: colors.highlightPure,
    error: colors.feedbackWarningPure,
    running: colors.feedbackInfoPure,
    pending: colors.feedbackInfoMedium,
    aborted: colors.neutralLowLight,
  },

  banner: {
    ...palettes.light.banner,
    info: colors.feedbackInfoPure,
    error: colors.feedbackWarningPure,
    text: colors.neutralHighPure,
    link: colors.neutralHighPure,
  },
};

// Flat navy page headers - DESIGN.md forbids gradients except subtle navy-on-navy,
// so start and end colors are identical (effectively flat).
const navyPage = genPageTheme({
  colors: [colors.brandPrimaryPure, colors.brandPrimaryPure],
  shape: shapes.wave,
});

const pageTheme = {
  home: navyPage,
  documentation: navyPage,
  tool: navyPage,
  service: navyPage,
  website: navyPage,
  library: navyPage,
  app: navyPage,
  apis: navyPage,
  other: navyPage,
};

export const pivotreeLightTheme: UnifiedTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: pivotreePalette,
    defaultPageTheme: 'home',
    pageTheme,
    typography: {
      htmlFontSize: typography.htmlFontSize,
      // TODO(brand-fonts): swap to self-hosted 'Montserrat' once the font files
      // are vendored under packages/app. DESIGN.md forbids Google Fonts / any CDN.
      fontFamily: '"Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
      // marginBottom is a required Backstage typography field, not a DESIGN.md
      // brand token; mirror Backstage's default heading spacing while keeping
      // the brand size/weight/line-height/letter-spacing from ./tokens.
      h1: { ...typography.h1, marginBottom: 10 },
      h2: { ...typography.h2, marginBottom: 8 },
      h3: { ...typography.h3, marginBottom: 6 },
      h4: { ...typography.h4, marginBottom: 6 },
      h5: { ...typography.h5, marginBottom: 4 },
      h6: { ...typography.h6, marginBottom: 2 },
    },
  }),
  components: {
    // Buttons - DESIGN.md button-primary (green), rounded.md, no uppercase.
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: rounded.md,
          textTransform: 'none',
          fontWeight: 400, // body-sm
          minHeight: 40, // button md
          paddingLeft: 16,
          paddingRight: 16,
        },
        sizeSmall: { minHeight: 32 }, // button sm
        sizeLarge: { minHeight: 48, paddingLeft: 20, paddingRight: 20 }, // button cta
        contained: { boxShadow: elevation.level1 },
        containedPrimary: {
          // button-primary-hover -> brand-secondary-medium
          '&:hover': { backgroundColor: colors.brandSecondaryMedium },
        },
      },
    },

    // Cards - rounded.lg, soft level-1 shadow.
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: rounded.lg, boxShadow: elevation.level1 },
      },
    },

    // Inputs - rounded.md.
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: rounded.md },
      },
    },

    // Tags / chips - pill, 24px (sm 20px), caption type.
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: rounded.pill, height: 24, fontSize: 12 },
        sizeSmall: { height: 20 },
      },
    },

    // Tabs - switch views within a context; no uppercase.
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 400, minHeight: 40 },
      },
    },

    // Tooltip - dark inverse surface, rounded.sm, caption.
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.neutralLowPure, // surface-inverse
          color: colors.neutralHighPure,
          borderRadius: rounded.sm,
          fontSize: 12,
          padding: 8,
        },
      },
    },

    // Divider - 1px border-subtle hairline.
    MuiDivider: {
      styleOverrides: {
        root: { backgroundColor: colors.neutralHighMedium },
      },
    },

    // Progress - pill track/fill, 8px.
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: rounded.pill, height: 8 },
        bar: { borderRadius: rounded.pill },
      },
    },

    // Modals - rounded.lg surface.
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: rounded.lg },
      },
    },

    // DataTable header on the subtle surface.
    MuiTableCell: {
      styleOverrides: {
        head: { backgroundColor: colors.neutralHighLight },
      },
    },
  },
});
